import api from "../api/api";

const MAX_FAMILY_WORKGROUP_PAGES = 500;
let familyWorkgroupCache = {
    token: "",
    list: null,
    loadingPromise: null,
};

function normalizeSearch(search) {
    return (search || "").trim().toLowerCase();
}

function matchesSearch(item, normalizedSearch) {
    if (!normalizedSearch) return true;
    return (item?.name || "").toLowerCase().includes(normalizedSearch);
}

function uniqueById(items) {
    const seen = new Set();
    const uniqueItems = [];

    (items || []).forEach((item) => {
        if (!item || seen.has(item.id)) return;
        seen.add(item.id);
        uniqueItems.push(item);
    });

    return uniqueItems;
}

function mapFamilyWorkgroupItem(item) {
    return {
        id: Number(item?.workgroupDUID ?? 0),
        name: (item?.workgroupName || "").trim() || "Unnamed Family Workgroup",
        _pickerType: "familyWorkgroup",
        workgroupDUID: item?.workgroupDUID,
        workgroupName: item?.workgroupName,
        recordCount: item?.recordCount,
    };
}

async function loadAllFamilyWorkgroups(parishSoftToken) {
    if (
        familyWorkgroupCache.list &&
        familyWorkgroupCache.token === parishSoftToken
    ) {
        return {
            success: true,
            list: familyWorkgroupCache.list,
            totalCount: familyWorkgroupCache.list.length,
        };
    }

    if (
        familyWorkgroupCache.loadingPromise &&
        familyWorkgroupCache.token === parishSoftToken
    ) {
        return familyWorkgroupCache.loadingPromise;
    }

    familyWorkgroupCache.token = parishSoftToken;
    familyWorkgroupCache.loadingPromise = (async () => {
        const allItems = [];
        let reportedTotalCount = null;

        for (let currentPage = 1; currentPage <= MAX_FAMILY_WORKGROUP_PAGES; currentPage++) {
            const result = await api.parishSoft.getFamilyWorkgroupList(
                parishSoftToken,
                currentPage,
            );

            if (!result?.success) {
                return {
                    success: false,
                    list: [],
                    totalCount: 0,
                    errorMessage:
                        result?.errorMessage || "Failed to load family workgroups.",
                };
            }

            const mappedPage = Array.isArray(result?.list)
                ? result.list.map(mapFamilyWorkgroupItem)
                : [];

            const firstRecordCount = mappedPage[0]?.recordCount;
            if (typeof result?.totalCount === "number") {
                reportedTotalCount = result.totalCount;
            } else if (typeof firstRecordCount === "number") {
                reportedTotalCount = firstRecordCount;
            }

            allItems.push(...mappedPage);

            if (mappedPage.length === 0) break;
            if (
                typeof reportedTotalCount === "number" &&
                allItems.length >= reportedTotalCount
            ) {
                break;
            }
        }

        const uniqueItems = uniqueById(allItems);
        familyWorkgroupCache.list = uniqueItems;

        return {
            success: true,
            list: uniqueItems,
            totalCount: uniqueItems.length,
        };
    })();

    const result = await familyWorkgroupCache.loadingPromise;
    familyWorkgroupCache.loadingPromise = null;

    if (!result?.success) {
        familyWorkgroupCache.list = null;
    }

    return result;
}

const familyWorkgroups = {
    load: async (search, page, pageSize, skipTotal) => {
        const parishSoftToken = localStorage.getItem("ParishSoftToken") || "";
        const result = await loadAllFamilyWorkgroups(parishSoftToken);

        if (result?.success) {
            const normalizedSearch = normalizeSearch(search);
            const filteredList = (result.list || []).filter((item) =>
                matchesSearch(item, normalizedSearch),
            );
            const currentPage = Math.max(1, Number(page) || 1);
            const currentPageSize = Math.max(1, Number(pageSize) || 50);
            const startIndex = (currentPage - 1) * currentPageSize;
            const pagedList = filteredList.slice(
                startIndex,
                startIndex + currentPageSize,
            );

            return {
                success: true,
                list: pagedList,
                totalCount: filteredList.length,
            };
        }

        return {
            success: false,
            list: [],
            totalCount: 0,
            errorMessage:
                result?.errorMessage || "Failed to load family workgroups.",
        };
    },
};

export default familyWorkgroups;
